import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Slider,
  Typography,
  Stack,
} from '@mui/material';
import { ZoomIn, ZoomOut, CropFree, CheckCircle } from '@mui/icons-material';

/**
 * Crop-to-ratio before upload.
 *
 * ── Why this exists ──────────────────────────────────────────────────────────
 * The member apps render a promo banner as a fixed-height, full-width strip with
 * BoxFit.cover. That is the right shape for a home screen — it stays a strip and
 * never takes the page over. The cost is that artwork of any other ratio gets
 * cropped by the app, blind: a portrait poster loses roughly half its height,
 * title and date included, and nobody sees it until the app is open.
 *
 * Letting the app adapt to the artwork instead was tried and was worse — a
 * near-square photo grew to swallow 57% of the home screen, and a poster ended
 * up floating in grey letterbox bands.
 *
 * So the crop happens HERE, where a human can see it and place it. The app keeps
 * its clean fixed strip, and every creative arrives already the right shape.
 *
 * Deliberately dependency-free: drag to reposition, slider to zoom, canvas to
 * export. Not worth adding a package to the admin panel for.
 */

/**
 * The apps draw the banner as a full-width strip of FIXED height, so the ratio
 * they actually render is screenWidth / stripHeight — and it differs by app:
 *
 *   Purple Pilates        280pt strip → 402/280 ≈ 1.44 on an iPhone 16 Pro
 *   Poise & Pulse, Laya   180pt strip → 402/180 ≈ 2.23
 *
 * We crop to the TALLEST of those (1.44), so nothing is ever letterboxed. The
 * slim-strip apps then trim the top and bottom with BoxFit.cover.
 */
export const BANNER_ASPECT = 1.44;
const OUT_WIDTH = 1440;
const OUT_HEIGHT = Math.round(OUT_WIDTH / BANNER_ASPECT); // 1000

interface Props {
  open: boolean;
  /** The picked file, shown from a local object URL — nothing is uploaded yet. */
  file: File | null;
  onCancel: () => void;
  onCropped: (file: File) => void;
}

const ImageCropDialog: React.FC<Props> = ({ open, file, onCancel, onCropped }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  /** Top-left of the scaled image relative to the viewport, in display px. */
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);

  const viewportRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const dragRef = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);

  // Local preview only — the crop is computed and uploaded on Apply.
  useEffect(() => {
    if (!file) { setObjectUrl(null); return; }
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    setNatural(null);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const viewport = () => {
    const el = viewportRef.current;
    if (!el) return { w: 0, h: 0 };
    return { w: el.clientWidth, h: el.clientHeight };
  };

  /** Scale at which the image exactly covers the viewport — zoom 1. */
  const baseScale = useCallback(() => {
    const { w, h } = viewport();
    if (!natural || !w || !h) return 1;
    return Math.max(w / natural.w, h / natural.h);
  }, [natural]);

  /** Keep the image covering the frame, so a crop can never include empty space. */
  const clampOffset = useCallback(
    (next: { x: number; y: number }, scale: number) => {
      const { w, h } = viewport();
      if (!natural) return next;
      const dispW = natural.w * scale;
      const dispH = natural.h * scale;
      return {
        x: Math.min(0, Math.max(w - dispW, next.x)),
        y: Math.min(0, Math.max(h - dispH, next.y)),
      };
    },
    [natural],
  );

  // Centre the image the moment its dimensions are known.
  const centre = useCallback((n: { w: number; h: number }) => {
    const { w, h } = viewport();
    if (!w || !h) { requestAnimationFrame(() => centre(n)); return; }
    const s = Math.max(w / n.w, h / n.h);
    setOffset({ x: (w - n.w * s) / 2, y: (h - n.h * s) / 2 });
  }, []);

  const handleImgLoad = () => {
    const el = imgRef.current;
    if (!el) return;
    const n = { w: el.naturalWidth, h: el.naturalHeight };
    setNatural(n);
    centre(n);
  };

  useEffect(() => {
    if (!natural) return;
    setOffset((o) => clampOffset(o, baseScale() * zoom));
  }, [zoom, natural, baseScale, clampOffset]);

  // Resizing the window resizes the frame, which can leave the image no longer
  // covering it — re-clamp so a crop can never pick up empty space.
  useEffect(() => {
    if (!natural) return;
    const onResize = () => setOffset((o) => clampOffset(o, baseScale() * zoom));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [natural, zoom, baseScale, clampOffset]);

  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const next = { x: d.ox + (e.clientX - d.x), y: d.oy + (e.clientY - d.y) };
    setOffset(clampOffset(next, baseScale() * zoom));
  };
  const onPointerUp = () => { dragRef.current = null; };

  const handleApply = async () => {
    if (!file || !natural || !imgRef.current) return;
    // Already exactly the export size — upload it untouched rather than
    // re-encoding a finished creative.
    if (
      natural.w === OUT_WIDTH &&
      natural.h === OUT_HEIGHT &&
      (file.type === 'image/jpeg' || file.type === 'image/png')
    ) {
      onCropped(file);
      return;
    }
    setBusy(true);
    try {
      const { w: vw, h: vh } = viewport();
      const scale = baseScale() * zoom;

      // The visible window, expressed back in the source image's own pixels.
      const sx = -offset.x / scale;
      const sy = -offset.y / scale;
      const sw = vw / scale;
      const sh = vh / scale;

      const canvas = document.createElement('canvas');
      canvas.width = OUT_WIDTH;
      canvas.height = OUT_HEIGHT;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas is unavailable in this browser');
      // A white ground, so a transparent PNG does not export as black.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, OUT_WIDTH, OUT_HEIGHT);
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(imgRef.current, sx, sy, sw, sh, 0, 0, OUT_WIDTH, OUT_HEIGHT);

      const blob: Blob = await new Promise((resolve, reject) =>
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('Could not render the crop'))),
          'image/jpeg',
          0.92,
        ),
      );
      const base = file.name.replace(/\.[^.]+$/, '');
      onCropped(new File([blob], `${base}-banner.jpg`, { type: 'image/jpeg' }));
    } finally {
      setBusy(false);
    }
  };

  const scale = natural ? baseScale() * zoom : 1;

  /** How far the framed region has to be stretched to fill 1440 × 1000. Above
   *  about 1.5× the banner visibly softens, which is worth saying before it is
   *  live on every member's home screen. */
  const upscale = natural && scale ? OUT_WIDTH / (viewport().w / scale) : 1;
  const soft = upscale > 1.5;

  /** Artwork already made at the banner ratio covers the frame exactly, so there
   *  is nothing to position — say so instead of showing crop controls. */
  const exactFit = !!natural && Math.abs(natural.w / natural.h - BANNER_ASPECT) < 0.01;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {exactFit ? <CheckCircle fontSize="small" color="success" /> : <CropFree fontSize="small" />}
          <Typography variant="h6" fontWeight={700}>
            {exactFit ? 'Perfect fit' : 'Position your banner'}
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {exactFit
            ? `This image is already the banner shape (${natural!.w} × ${natural!.h}px), so nothing will be cropped.`
            : 'Drag to reposition, zoom to fit.'}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box
          ref={viewportRef}
          onPointerDown={exactFit ? undefined : onPointerDown}
          onPointerMove={exactFit ? undefined : onPointerMove}
          onPointerUp={exactFit ? undefined : onPointerUp}
          onPointerCancel={exactFit ? undefined : onPointerUp}
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: `${BANNER_ASPECT}`,
            overflow: 'hidden',
            borderRadius: 2,
            bgcolor: '#111',
            cursor: exactFit ? 'default' : 'grab',
            '&:active': { cursor: exactFit ? 'default' : 'grabbing' },
            touchAction: 'none',
            userSelect: 'none',
          }}
        >
          {objectUrl && (
            <Box
              component="img"
              ref={imgRef}
              src={objectUrl}
              alt="crop source"
              onLoad={handleImgLoad}
              draggable={false}
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                transformOrigin: 'top left',
                transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                width: natural ? `${natural.w}px` : 'auto',
                height: natural ? `${natural.h}px` : 'auto',
                maxWidth: 'none',
                pointerEvents: 'none',
              }}
            />
          )}
          {!exactFit && (<>
          {/* Rule of thirds — helps line a subject up without obscuring the art. */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              backgroundImage:
                'linear-gradient(to right, rgba(255,255,255,.28) 1px, transparent 1px),' +
                'linear-gradient(to bottom, rgba(255,255,255,.28) 1px, transparent 1px)',
              backgroundSize: '33.333% 33.333%',
            }}
          />
          </>)}
        </Box>

        {exactFit ? (
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 2 }}>
            Uploads as-is — the exact shape the apps render.
          </Typography>
        ) : (<>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
          <ZoomOut fontSize="small" color="action" />
          <Slider
            value={zoom}
            min={1}
            max={4}
            step={0.01}
            onChange={(_, v) => setZoom(v as number)}
            aria-label="Zoom"
          />
          <ZoomIn fontSize="small" color="action" />
        </Stack>

        {soft ? (
          <Typography variant="caption" color="warning.main">
            This crop is being enlarged {upscale.toFixed(1)}× to reach {OUT_WIDTH} × {OUT_HEIGHT}px, so it
            will look soft. Zoom out, or use a larger image.
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Exports at {OUT_WIDTH} × {OUT_HEIGHT}px — the exact shape the apps render.
          </Typography>
        )}
        </>)}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={handleApply} disabled={busy || !natural}>
          {busy ? 'Preparing…' : exactFit ? 'Use this image' : 'Use this crop'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropDialog;
